<template>
    <div>
        <b-alert v-if="actionError" show variant="danger" class="request-actions-inline-feedback">
            {{ actionError }}
        </b-alert>
        <b-alert
            v-if="mobileSuccessMessage"
            show
            variant="success"
            class="request-actions-inline-feedback"
        >
            <i class="fas fa-check mr-1"></i>
            {{ mobileSuccessMessage }}
        </b-alert>

        <div class="text-right">
            <!-- Основные действия -->
            <b-button 
                v-for="action in visiblePrimaryActionButtons"
                :key="action.key" 
                :variant="action.variant"
                :disabled="action.disabled" 
                @click="handleAction(action.key)" 
                size="sm" 
                class="mr-2 mt-2"
            >
                <template v-if="action.loading">
                    <b-spinner small class="mr-1"></b-spinner>
                    <span class="d-none d-sm-inline">{{ action.loadingText || action.text }}</span>
                </template>
                <template v-else>
                    <i :class="action.icon"></i>
                    <span class="ml-1">{{ action.text }}</span>
                </template>
            </b-button>

            <!-- Второстепенные действия -->
            <b-button 
                v-for="action in visibleSecondaryActionButtons"
                :key="action.key" 
                :variant="action.variant"
                :disabled="action.disabled" 
                @click="handleAction(action.key)" 
                size="sm" 
                class="mr-2 mt-2"
            >
                <template v-if="action.loading">
                    <b-spinner small class="mr-1"></b-spinner>
                    {{ action.loadingText || action.text }}
                </template>
                <template v-else>
                    <i :class="action.icon"></i> {{ action.text }}
                </template>
            </b-button>

            <!-- Связанные заявки -->
            <b-button
                v-if="visibleLinkedRequestRules.length === 1"
                variant="outline-primary"
                @click="openLinkedRequestForm(visibleLinkedRequestRules[0])"
                size="sm"
                class="mr-2 mt-2"
            >
                <i class="fas fa-link"></i>
                {{ visibleLinkedRequestRules[0].label }}
            </b-button>

            <b-dropdown
                v-else-if="visibleLinkedRequestRules.length > 1"
                variant="outline-primary"
                size="sm"
                class="mr-2 mt-2"
                right
            >
                <template #button-content>
                    <i class="fas fa-link"></i>
                    Создать связанную
                </template>
                <b-dropdown-item-button
                    v-for="rule in visibleLinkedRequestRules"
                    :key="rule.key"
                    @click="openLinkedRequestForm(rule)"
                >
                    {{ rule.label }}
                </b-dropdown-item-button>
            </b-dropdown>
        </div>

        <RequestFulfillment ref="fulfillment" :request="request" :before-action="beforeAction" @open-materials="$emit('open-materials')" />

        <RequestDeadlineModal v-model="showDeadlineModal" :request="request" :saving="deadlineModalBusy"
            :error="deadlineError" :can-edit="canEditRequestDeadline" :comment-required="submitCommentRequired"
            :initial-comment="deadlineComment" :server-now="deadlineServerNow" submit-mode @confirm="confirmDeadlineSubmit" />

        <!-- Модальные окна -->
        <ApproveModal
            ref="approveModal"
            v-model="showApproveModal"
            :loading="approvingRequest"
            :required="approveCommentRequired"
            @confirm="handleApproveConfirm"
        />

        <ReturnModal
            v-model="showReturnModal"
            :loading="returningRequest"
            @confirm="handleReturnConfirm"
        />

        <ClosingActionModal
            :key="closingActionKey || 'closing-action'"
            ref="closingActionModal"
            v-model="showClosingActionModal"
            :loading="closingActionLoading"
            :required="closingActionCommentRequired"
            :title="closingActionConfig.title"
            :alert-text="closingActionConfig.alertText"
            :comment-label="closingActionConfig.commentLabel"
            :placeholder="closingActionConfig.placeholder"
            :confirm-text="closingActionConfig.confirmText"
            :loading-text="closingActionConfig.loadingText"
            :variant="closingActionConfig.variant"
            @confirm="handleClosingActionConfirm"
        />

        <ChangeStatusModal
            v-model="showChangeStatusModal"
            :loading="changingStatus"
            :current-status="request.status"
            :current-status-label="request.status_label || getStatusText(request.status)"
            :current-status-variant="request.status_variant || getStatusVariant(request.status)"
            :closing-comment-required="closingCommentRequired"
            :allow-close-without-mechanic-actions="request.RequestType && request.RequestType.workflow_config && request.RequestType.workflow_config.itemCatalog && request.RequestType.workflow_config.itemCatalog.enabled && request.RequestType.workflow_config.itemCatalog.type === 'mechanic_assets'"
            @confirm="handleChangeStatusConfirm"
        />
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import RequestDeadlineModal from '../../components/RequestDeadlineModal.vue';
import {deadlinePolicy, DEADLINE_CODES} from '@/utils/requestDeadline';
import ApproveModal from './modals/ApproveModal.vue';
import RequestFulfillment from './RequestFulfillment.vue';
import ReturnModal from './modals/ReturnModal.vue';
import ClosingActionModal from './modals/ClosingActionModal.vue';
import ChangeStatusModal from './modals/ChangeStatusModal.vue';
import { getStatusText, getStatusVariant } from '@/utils/requestCard/formatters';
import {
    actionClosesRequest,
    isRequestActionCommentRequired
} from '@/utils/requestClosingComment';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';

/**
 * Компонент кнопок действий с заявкой
 * Полностью автономный — содержит всю логику действий, модалки и уведомления
 */
export default {
    name: 'RequestActions',
    components: {
        RequestDeadlineModal,
        RequestFulfillment,
        ApproveModal,
        ReturnModal,
        ClosingActionModal,
        ChangeStatusModal
    },
    props: {
        saveHandler: {type: Function, default: null},
        beforeAction: {type: Function, default: null},
        selfAssignPreviewEnabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showDeadlineModal: false, deadlineModalBusy: false, deadlineError: '', deadlineComment: '', deadlineServerNow: null,
            showApproveModal: false,
            showReturnModal: false,
            showClosingActionModal: false,
            closingActionKey: null,
            showChangeStatusModal: false,
            mobileSuccessMessage: '',
            actionError: '',
            mobileSuccessTimer: null
        };
    },
    computed: {
        canEditRequestDeadline() { return this.$store.getters['requestCard/canEditRequestDeadline']; },
        submitCommentRequired() {
            return actionClosesRequest(this.request, 'submit') || isRequestActionCommentRequired(this.request, 'submit');
        },
        ...mapState('requestCard', [
            'request',
            'submittingRequest',
            'approvingRequest',
            'returningRequest',
            'changingStatus',
            'executingRequest',
            'rejectingRequest',
            'completingRequest',
            'partiallyCompletingRequest',
            'acceptingRequest',
            'acceptingPartiallyRequest',
            'cancellingRequest'
        ]),
        ...mapGetters('requestCard', [
            'primaryActionButtons',
            'secondaryActionButtons',
            'hasFormChanges'
        ]),
        visiblePrimaryActionButtons() {
            return this.filterPreviewActions(this.primaryActionButtons).filter(action => !this.saveHandler || action.key !== 'save');
        },
        visibleSecondaryActionButtons() {
            return this.filterPreviewActions(this.secondaryActionButtons);
        },
        visibleLinkedRequestRules() {
            const rules = this.request && Array.isArray(this.request.available_linked_request_rules)
                ? this.request.available_linked_request_rules
                : [];
            return this.selfAssignPreviewEnabled ? [] : rules;
        },
        closingCommentRequired() {
            return isRequestActionCommentRequired(this.request, 'change_status', 'completed');
        },
        approveCommentRequired() {
            return isRequestActionCommentRequired(this.request, 'approve');
        },
        isMultiExecutorCompletion() {
            return this.request?.is_multi_executor === true;
        },
        completeWillBePartial() {
            return this.request?.permissions?.completeWillBePartial === true;
        },
        closingActionCommentRequired() {
            const actionKey = this.closingActionKey === 'complete' && this.completeWillBePartial
                ? 'partially_complete'
                : this.closingActionKey;
            return isRequestActionCommentRequired(this.request, actionKey);
        },
        closingActionLoading() {
            const flags = {
                submit: this.submittingRequest,
                reject: this.rejectingRequest,
                execute: this.executingRequest,
                complete: this.completingRequest,
                partially_complete: this.partiallyCompletingRequest,
                accept: this.acceptingRequest,
                accept_partially: this.acceptingPartiallyRequest,
                cancel: this.cancellingRequest
            };
            return Boolean(flags[this.closingActionKey]);
        },
        closingActionConfig() {
            const completeClosesRequest = actionClosesRequest(this.request, 'complete');
            const partialCompleteClosesRequest = actionClosesRequest(this.request, 'partially_complete');
            const definitions = {
                submit: {
                    title: 'Отправка заявки',
                    alertText: 'Действие переведёт заявку непосредственно в закрывающий статус.',
                    commentLabel: 'Комментарий к отправке',
                    placeholder: 'Добавьте пояснение к отправке заявки…',
                    confirmText: 'Отправить заявку',
                    loadingText: 'Отправка…',
                    variant: 'primary'
                },
                reject: {
                    title: 'Отклонение заявки',
                    alertText: 'Заявка будет отклонена и закрыта. Это действие необратимо.',
                    commentLabel: 'Причина отказа',
                    placeholder: 'Укажите причину отказа по заявке…',
                    confirmText: 'Отклонить',
                    loadingText: 'Отклонение…',
                    variant: 'danger'
                },
                execute: {
                    title: 'Взятие заявки в работу',
                    alertText: 'Действие переведёт заявку непосредственно в закрывающий статус.',
                    commentLabel: 'Комментарий к действию',
                    placeholder: 'Добавьте пояснение…',
                    confirmText: 'Выполнить действие',
                    loadingText: 'Выполнение…',
                    variant: 'primary'
                },
                complete: {
                    title: completeClosesRequest ? 'Завершение заявки' : 'Отправка на приёмку',
                    alertText: completeClosesRequest
                        ? 'Заявка будет переведена в статус «Завершена» и закрыта.'
                        : 'Результат выполнения будет отправлен инициатору на полную приёмку.',
                    commentLabel: completeClosesRequest ? 'Комментарий к завершению' : 'Комментарий к выполнению',
                    placeholder: 'Опишите результат выполнения…',
                    confirmText: completeClosesRequest ? 'Завершить заявку' : 'Отправить на приёмку',
                    loadingText: completeClosesRequest ? 'Завершение…' : 'Отправка…',
                    variant: 'success'
                },
                partially_complete: {
                    title: partialCompleteClosesRequest ? 'Завершение заявки' : 'Отправка на частичную приёмку',
                    alertText: partialCompleteClosesRequest
                        ? 'Это действие переведёт заявку в закрывающий статус.'
                        : 'Выполненная часть заявки будет отправлена инициатору на частичную приёмку.',
                    commentLabel: partialCompleteClosesRequest ? 'Комментарий к завершению' : 'Комментарий к выполненной части',
                    placeholder: 'Опишите результат выполнения…',
                    confirmText: partialCompleteClosesRequest ? 'Подтвердить' : 'Отправить на приёмку',
                    loadingText: partialCompleteClosesRequest ? 'Завершение…' : 'Отправка…',
                    variant: 'success'
                },
                accept: {
                    title: 'Подтверждение выполнения',
                    alertText: 'Выполнение будет подтверждено, заявка завершена и закрыта.',
                    commentLabel: 'Комментарий к приемке',
                    placeholder: 'Добавьте комментарий к результату выполнения…',
                    confirmText: 'Подтвердить выполнение',
                    loadingText: 'Подтверждение…',
                    variant: 'success'
                },
                accept_partially: {
                    title: 'Принятие результата',
                    alertText: 'Результат будет принят, заявка завершена и закрыта.',
                    commentLabel: 'Комментарий к приемке',
                    placeholder: 'Добавьте комментарий к принятому результату…',
                    confirmText: 'Принять результат',
                    loadingText: 'Принятие…',
                    variant: 'success'
                },
                cancel: {
                    title: 'Отмена заявки',
                    alertText: 'Заявка будет отменена и закрыта.',
                    commentLabel: 'Причина отмены',
                    placeholder: 'Укажите причину отмены заявки…',
                    confirmText: 'Отменить заявку',
                    loadingText: 'Отмена…',
                    variant: 'danger'
                }
            };
            if (this.isMultiExecutorCompletion) {
                definitions.complete = {
                    title: 'Завершение своей части',
                    alertText: this.request?.status === 'awaiting_acceptance'
                        ? 'Ваша часть будет отмечена выполненной. После этого инициатор сможет подтвердить выполнение заявки.'
                        : (this.completeWillBePartial
                            ? 'Ваша часть будет отмечена выполненной. Заявка останется в работе у остальных исполнителей.'
                            : 'Ваша часть будет отмечена выполненной. Заявка перейдёт на приёмку.'),
                    commentLabel: 'Комментарий к выполнению своей части',
                    placeholder: 'Опишите результат выполнения…',
                    confirmText: 'Завершить свою часть',
                    loadingText: 'Завершение…',
                    variant: 'success'
                };
            }
            return definitions[this.closingActionKey] || {
                title: 'Закрытие заявки',
                alertText: 'Заявка будет переведена в закрывающий статус.',
                commentLabel: 'Комментарий',
                placeholder: 'Введите комментарий…',
                confirmText: 'Подтвердить',
                loadingText: 'Сохранение…',
                variant: 'success'
            };
        }
    },
    beforeDestroy() {
        if (this.mobileSuccessTimer) {
            clearTimeout(this.mobileSuccessTimer);
        }
    },
    watch: {
        actionError(value) { this.$emit('action-error', value); }
    },
    methods: {
        confirmAnnulment() { return this.$bvModal.msgBoxConfirm('После окончательного согласования отчёты будут аннулированы и исключены из расчётов. Отменить это через интерфейс нельзя. Продолжить?', { contentClass: 'argus-glass-dialog',okTitle: 'Продолжить', cancelTitle: 'Отмена', okVariant: 'danger', centered: true}); },
        ...mapActions('requestCard', [
            'requestCard_saveRequest',
            'requestCard_submitRequest',
            'requestCard_approveRequest',
            'requestCard_rejectRequest',
            'requestCard_executeRequest',
            'requestCard_returnRequest',
            'requestCard_completeRequest',
            'requestCard_partiallyCompleteRequest',
            'requestCard_acceptRequest',
            'requestCard_acceptPartiallyRequest',
            'requestCard_cancelRequest',
            'requestCard_changeRequestStatus'
        ]),
        getStatusText,
        getStatusVariant,
        filterPreviewActions(actions) {
            const fulfillment = this.request.fulfillment;
            return actions.filter(action => {
                if (this.selfAssignPreviewEnabled && ['enable_self_assign_mode', 'execute'].includes(action.key)) return false;
                if (!fulfillment) return true;
                if (['accept', 'accept_partially'].includes(action.key)) return false;
                if (['complete', 'partially_complete'].includes(action.key) && (fulfillment.config.shipping || fulfillment.review)) return false;
                if (['return', 'execute', 'enable_self_assign_mode'].includes(action.key) && fulfillment.review) return false;
                return true;
            });
        },

        isMobileToastAlternative() {
            if (typeof window === 'undefined') {
                return false;
            }
            if (window.matchMedia) {
                return window.matchMedia('(max-width: 767.98px)').matches;
            }
            return window.innerWidth < 768;
        },

        showSuccessFeedback(message) {
            if (!this.isMobileToastAlternative()) {
                this.$bvToast && this.$bvToast.toast(message, {
                    title: 'Успех',
                    variant: 'success',
                    solid: true
                });
                notifyInstallPromotionSuccess({source: 'request-action'});
                return;
            }

            this.mobileSuccessMessage = message;
            if (this.mobileSuccessTimer) {
                clearTimeout(this.mobileSuccessTimer);
            }
            this.mobileSuccessTimer = setTimeout(() => {
                this.mobileSuccessMessage = '';
                this.mobileSuccessTimer = null;
            }, 2200);
            notifyInstallPromotionSuccess({source: 'request-action'});
        },

        /**
         * Обработчик клика по кнопке действия
         */
        async handleAction(actionKey) {
            if (this.beforeAction && !['save', 'copy', 'enable_self_assign_mode'].includes(actionKey)) {
                if (!await this.beforeAction(actionKey)) return;
            }
            const handlers = {
                'submit': this.submitRequest,
                'save': this.saveRequest,
                'approve': () => { this.showApproveModal = true; },
                'reject': () => this.openClosingAction('reject'),
                'execute': this.executeRequest,
                'enable_self_assign_mode': this.enableSelfAssignPreview,
                'return': () => { this.showReturnModal = true; },
                'complete': this.completeRequest,
                'partially_complete': this.partiallyCompleteRequest,
                'accept': this.acceptRequest,
                'accept_partially': this.acceptPartiallyRequest,
                'cancel': () => this.openClosingAction('cancel'),
                'copy': this.openCopyModal,
                'change_status': () => { this.showChangeStatusModal = true; }
            };

            const handler = handlers[actionKey];
            if (handler) {
                handler();
            }
        },

        /**
         * Универсальный метод для действий с подтверждением
         */
        async callAction(actionDispatch, confirmMessage, confirmTitle, successMsg, okVariant = 'primary') {
            this.actionError = '';
            const confirmed = await this.$bvModal.msgBoxConfirm(confirmMessage, { contentClass: 'argus-glass-dialog',
                title: confirmTitle,
                size: 'sm',
                buttonSize: 'sm',
                okVariant: okVariant,
                okTitle: 'Да, выполнить',
                cancelTitle: 'Отмена',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            });

            if (!confirmed) return;

            try {
                const result = await actionDispatch();
                if (this.handleSaveResult(result)) return;
                this.showSuccessFeedback(successMsg);
            } catch (error) {
                const errorMsg = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : error.message || 'Не удалось выполнить действие';
                this.actionError = errorMsg;
            }
        },

        handleSaveResult(result) {
            if (result && result.lostAccess) {
                this.showDeadlineModal = false;
                this.showClosingActionModal = false;
                this.$bvToast && this.$bvToast.toast('Изменения сохранены. После смены объекта доступ к заявке изменился.', {
                    title: 'Заявка сохранена', variant: 'success', solid: true
                });
                this.$router.push('/requests');
                return true;
            }
            if (result && result.refreshFailed) {
                this.actionError = result.message;
                return true;
            }
            return false;
        },

        async saveRequest() {
            if (this.saveHandler) return this.saveHandler();
            this.actionError = '';
            if (!this.hasFormChanges) {
                return;
            }

            try {
                const result = await this.requestCard_saveRequest();
                if (this.handleSaveResult(result)) return;
                this.showSuccessFeedback('Заявка сохранена');
                this.$emit('saved', {refreshed: true});
            } catch (error) {
                const errorMsg = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : error.message || 'Не удалось сохранить заявку';
                this.actionError = errorMsg;
            }
        },

        async submitRequest() {
            if (this.submittingRequest || this.deadlineModalBusy) return;
            if (this.request.workflow_data && this.request.workflow_data.domain === 'report_annulment' && !await this.confirmAnnulment()) return;
            const recurrence = this.request.workflow_data && this.request.workflow_data.recurrence;
            if (!recurrence && deadlinePolicy(this.request.RequestType || {}, this.request).code) {
                this.deadlineError = ''; this.deadlineComment = ''; this.showDeadlineModal = true;
                return;
            }
            if (
                actionClosesRequest(this.request, 'submit') ||
                isRequestActionCommentRequired(this.request, 'submit')
            ) {
                this.openClosingAction('submit');
                return;
            }
            return this.callAction(
                () => this.sendRequest(),
                'Вы уверены, что хотите отправить заявку на согласование? Несохраненные изменения будут сохранены перед отправкой. Заявка будет направлена согласующим согласно настроенному процессу. После отправки редактирование будет ограничено.',
                'Отправка на согласование',
                'Заявка отправлена'
            );
        },

        async sendRequest(options = {}) {
            try { return await this.requestCard_submitRequest(options); }
            catch (error) {
                const data = error.response && error.response.data || {};
                if (DEADLINE_CODES.includes(data.code)) {
                    this.deadlineComment = options.comment || '';
                    this.deadlineServerNow = data.details && data.details.serverNow || null;
                    this.deadlineError = data.message || error.message;
                    this.showClosingActionModal = false;
                    this.showDeadlineModal = true;
                }
                throw error;
            }
        },
        async confirmDeadlineSubmit({comment, ...deadlineCorrection}) {
            if (this.deadlineModalBusy || this.submittingRequest) return;
            this.deadlineModalBusy = true; this.deadlineError = '';
            try {
                const result = await this.requestCard_submitRequest({comment, deadlineCorrection});
                if (this.handleSaveResult(result)) return;
                this.deadlineModalBusy = false;
                this.showDeadlineModal = false;
                this.actionError = '';
                this.showSuccessFeedback('Заявка отправлена');
            } catch (error) {
                const data = error.response && error.response.data || {};
                this.deadlineServerNow = data.details && data.details.serverNow || null;
                this.deadlineError = (error.submissionUncertain ? 'Не удалось проверить результат отправки. Проверьте состояние карточки перед повторной попыткой. ' :
                    (error.deadlineSaved ? 'Изменения сохранены. Заявка ещё не отправлена. ' : '')) +
                    (data.message || error.message || 'Не удалось отправить заявку');
            } finally { this.deadlineModalBusy = false; }
        },

        async approveRequest() {
            this.showApproveModal = true;
        },

        async handleApproveConfirm(comment) {
            if (this.approvingRequest) return;
            if (this.request.workflow_data && this.request.workflow_data.domain === 'report_annulment' && !await this.confirmAnnulment()) return;
            try {
                await this.requestCard_approveRequest({ comment });
                this.showSuccessFeedback('Решение о согласовании сохранено');
                this.showApproveModal = false;
                if (this.$refs.approveModal) this.$refs.approveModal.reset();
            } catch (error) {
                const errorMsg = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Не удалось согласовать заявку';
                this.$bvToast && this.$bvToast.toast(errorMsg, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            }
        },

        async executeRequest() {
            if (
                actionClosesRequest(this.request, 'execute') ||
                isRequestActionCommentRequired(this.request, 'execute')
            ) {
                this.openClosingAction('execute');
                return;
            }
            return this.callAction(
                () => this.requestCard_executeRequest(),
                'Вы уверены, что хотите взять эту заявку в работу? Заявка будет переведена в статус \'В работе\', и вы станете ответственным исполнителем.',
                'Взятие заявки в работу',
                'Заявка взята в работу'
            );
        },

        enableSelfAssignPreview() {
            this.$emit('self-assign-preview-enabled');
        },

        async handleReturnConfirm(comment) {
            try {
                await this.requestCard_returnRequest({ comment });
                this.showSuccessFeedback('Заявка возвращена на доработку');
                this.showReturnModal = false;
            } catch (error) {
                const errorMsg = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Не удалось вернуть заявку на доработку';
                this.$bvToast && this.$bvToast.toast(errorMsg, { 
                    title: 'Ошибка', 
                    variant: 'danger', 
                    solid: true 
                });
            }
        },

        async completeRequest() {
            this.openClosingAction('complete');
        },

        async partiallyCompleteRequest() {
            this.openClosingAction('partially_complete');
        },

        async acceptRequest() {
            if (this.request.fulfillment && this.request.fulfillment.config.acceptance) { this.openClosingAction('accept'); return; }
            if (
                actionClosesRequest(this.request, 'accept') ||
                isRequestActionCommentRequired(this.request, 'accept')
            ) {
                this.openClosingAction('accept');
                return;
            }
            return this.callAction(
                () => this.requestCard_acceptRequest(),
                'Вы уверены, что хотите подтвердить выполнение заявки? Заявка будет переведена в статус \'Завершена\' и закрыта. Это действие завершит процесс обработки заявки.',
                'Подтверждение выполнения',
                'Выполнение заявки подтверждено'
            );
        },

        async acceptPartiallyRequest() {
            if (this.request.fulfillment && this.request.fulfillment.config.acceptance) { this.openClosingAction('accept_partially'); return; }
            if (
                actionClosesRequest(this.request, 'accept_partially') ||
                isRequestActionCommentRequired(this.request, 'accept_partially')
            ) {
                this.openClosingAction('accept_partially');
                return;
            }
            return this.callAction(
                () => this.requestCard_acceptPartiallyRequest(),
                'Вы уверены, что хотите принять частично выполненную заявку? Заявка будет частично принята, но останется открытой для завершения оставшейся части работ.',
                'Частичное принятие',
                'Частично выполненная заявка принята'
            );
        },

        openClosingAction(actionKey) {
            const f = this.request.fulfillment;
            const stage = ['complete', 'partially_complete'].includes(actionKey) ? 'shipping' : (['accept', 'accept_partially'].includes(actionKey) ? 'acceptance' : null);
            if (f && stage && f.config[stage]) { this.$refs.fulfillment.open(stage, actionKey); return; }
            this.closingActionKey = actionKey;
            this.showClosingActionModal = true;
        },

        async handleClosingActionConfirm(comment) {
            if (!this.closingActionKey || this.closingActionLoading) return;
            const dispatchers = {
                submit: this.sendRequest,
                reject: this.requestCard_rejectRequest,
                execute: this.requestCard_executeRequest,
                complete: this.requestCard_completeRequest,
                partially_complete: this.requestCard_partiallyCompleteRequest,
                accept: this.requestCard_acceptRequest,
                accept_partially: this.requestCard_acceptPartiallyRequest,
                cancel: this.requestCard_cancelRequest
            };
            const successMessages = {
                submit: 'Заявка отправлена',
                reject: 'Заявка отклонена',
                execute: 'Действие выполнено',
                complete: actionClosesRequest(this.request, 'complete')
                    ? 'Заявка выполнена'
                    : 'Заявка отправлена на приёмку',
                partially_complete: actionClosesRequest(this.request, 'partially_complete')
                    ? 'Заявка завершена'
                    : 'Выполненная часть отправлена на приёмку',
                accept: 'Выполнение заявки подтверждено',
                accept_partially: 'Результат принят',
                cancel: 'Заявка отменена'
            };
            if (this.isMultiExecutorCompletion) {
                successMessages.complete = this.completeWillBePartial
                    ? 'Ваша часть выполнена, заявка осталась у других исполнителей'
                    : 'Ваша часть выполнена, заявка отправлена на приёмку';
            }
            const errorMessages = {
                submit: 'Не удалось отправить заявку',
                reject: 'Не удалось отклонить заявку',
                execute: 'Не удалось выполнить действие',
                complete: 'Не удалось завершить заявку',
                partially_complete: 'Не удалось завершить заявку',
                accept: 'Не удалось подтвердить выполнение',
                accept_partially: 'Не удалось принять результат',
                cancel: 'Не удалось отменить заявку'
            };
            const actionKey = this.closingActionKey;
            const dispatchAction = dispatchers[actionKey];
            if (!dispatchAction) return;

            try {
                const result = await dispatchAction({ comment });
                if (this.handleSaveResult(result)) return;
                this.showSuccessFeedback(successMessages[actionKey] || 'Заявка закрыта');
                this.showClosingActionModal = false;
                if (this.$refs.closingActionModal) this.$refs.closingActionModal.reset();
                this.closingActionKey = null;
            } catch (error) {
                const errorMsg = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : (errorMessages[actionKey] || 'Не удалось закрыть заявку');
                this.$bvToast && this.$bvToast.toast(errorMsg, {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            }
        },

        openCopyModal() {
            this.$router.push({ 
                path: '/requests/create', 
                query: { copyFrom: this.request.id } 
            });
        },
        hasDirtyState() {
            return (this.$refs.fulfillment && this.$refs.fulfillment.show) || this.showDeadlineModal || this.showApproveModal ||
                this.showReturnModal ||
                this.showClosingActionModal ||
                this.showChangeStatusModal;
        },

        openLinkedRequestForm(rule) {
            if (!rule || !rule.key || !this.request || !this.request.id) {
                return;
            }

            const targetRequestTypeId = rule.targetRequestTypeId || rule.target_request_type_id || rule.request_type_id;
            if (!targetRequestTypeId) {
                this.$bvToast && this.$bvToast.toast('В правиле не указан целевой тип заявки', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
                return;
            }

            this.$router.push({
                name: 'create-request',
                query: {
                    sourceRequestId: String(this.request.id),
                    linkRuleKey: rule.key,
                    requestTypeId: String(targetRequestTypeId)
                }
            }).catch(() => {});
        },

        async handleChangeStatusConfirm({ targetStatus, comment, closeWithoutMechanicActions }) {
            try {
                await this.requestCard_changeRequestStatus({ targetStatus, comment, closeWithoutMechanicActions });
                this.showSuccessFeedback('Статус изменен');
                this.showChangeStatusModal = false;
            } catch (error) {
                const errorMsg = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Не удалось изменить статус заявки';
                this.$bvToast && this.$bvToast.toast(errorMsg, { 
                    title: 'Ошибка', 
                    variant: 'danger', 
                    solid: true 
                });
            }
        }
    }
};
</script>

<style scoped>
.request-actions-inline-feedback {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    margin: 8px 0 0;
    padding: 6px 10px;
    border-radius: 4px;
    font-weight: 600;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

@media (max-width: 767.98px) {
    .request-actions-inline-feedback {
        display: flex;
        width: 100%;
    }
}
</style>
